import { get, isString } from "lodash-es";

import { audioRecordingModule } from "@/modules/audio-recording";
import { api } from "@/modules/api";
import { retranscribeFromSource } from "@/modules/audio-recording/retranscribe-from-source";

const DEFAULT_RETRANSCRIBE_ERROR_MESSAGE = "Retranscribe request failed. Please try again.";

type RetranscribeVoiceSourceArgs = {
  downloadUrl?: string | null;
};

type RetranscribeVoiceSourceResult =
  | { success: true; transcriptContent?: string }
  | { success: false; errorMessage: string };

enum AudioRecordingArtifactStrategy {
  Current = "CURRENT",
  Legacy = "LEGACY",
}

type RetranscribeTarget =
  | {
      strategy: AudioRecordingArtifactStrategy.Legacy;
      capturedAudioId: string;
    }
  | {
      strategy: AudioRecordingArtifactStrategy.Current;
      audioRecordingId: string;
      blobName: string;
    };

const resolveRetranscribeTarget = ({
  downloadUrl,
}: {
  downloadUrl: string;
}): RetranscribeTarget | null => {
  const blobName = audioRecordingModule.extractBlobNameFromDownloadUrl({ downloadUrl });
  if (!blobName) return null;

  const capturedAudioId = audioRecordingModule.extractCapturedAudioIdFromBlobName({ blobName });
  if (capturedAudioId) {
    return {
      strategy: AudioRecordingArtifactStrategy.Legacy,
      capturedAudioId,
    };
  }

  const audioRecordingId = audioRecordingModule.extractAudioRecordingIdFromBlobName({
    blobName,
  });
  if (!audioRecordingId) return null;

  return {
    strategy: AudioRecordingArtifactStrategy.Current,
    audioRecordingId,
    blobName,
  };
};

const extractRetranscribeErrorMessage = ({ rawError }: { rawError: unknown }): string => {
  const errorMessage = get(rawError, "error_message");
  if (isString(errorMessage)) {
    return errorMessage;
  }

  const detail = get(rawError, "detail");
  if (isString(detail)) {
    return detail;
  }

  return DEFAULT_RETRANSCRIBE_ERROR_MESSAGE;
};

export const retranscribeVoiceSource = async ({
  downloadUrl,
}: RetranscribeVoiceSourceArgs): Promise<RetranscribeVoiceSourceResult> => {
  if (!downloadUrl) {
    return {
      success: false,
      errorMessage: DEFAULT_RETRANSCRIBE_ERROR_MESSAGE,
    };
  }

  const target = resolveRetranscribeTarget({ downloadUrl });
  if (!target) {
    return {
      success: false,
      errorMessage: DEFAULT_RETRANSCRIBE_ERROR_MESSAGE,
    };
  }

  if (target.strategy === AudioRecordingArtifactStrategy.Legacy) {
    const response = await api.post("/v2/voice/transcribe", {
      body: { captured_audio_id: target.capturedAudioId, force: true },
    });

    if (!response.response.ok) {
      return {
        success: false,
        errorMessage: extractRetranscribeErrorMessage({ rawError: response.error }),
      };
    }

    return { success: true };
  }

  const response = await retranscribeFromSource({
    audioRecordingId: target.audioRecordingId,
    sourceBlobName: target.blobName,
  });

  if (!response.success) {
    return {
      success: false,
      errorMessage: response.errorMessage,
    };
  }

  return { success: true, transcriptContent: response.transcriptContent };
};

export const getRetranscribeErrorMessageFromUnknown = (error: unknown): string => {
  if (error instanceof Error && isString(error.message) && error.message) {
    return error.message;
  }

  return extractRetranscribeErrorMessage({ rawError: error });
};

export { DEFAULT_RETRANSCRIBE_ERROR_MESSAGE };
